import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAccounts } from '../lib/useAccounts';
import { useWorkflow } from '../WorkflowContext';
import Tag from '../components/Tag';

const FLOW = [
  ['1. Intake', 'Request captured from email, phone, text, form, portal, or verbal entry.'],
  ['2. AI Triage', 'AI summarizes, classifies, identifies missing data, and recommends routing.'],
  ['3. Task', 'Engine creates assignable work with one owner, status, and due date.'],
  ['4. Calendar', 'Deadline syncs to shared or staff calendar with reminders.'],
  ['5. Alerts', 'Staff gets notification and must acknowledge ownership.'],
  ['6. CRM Sync', 'Client profile, task board, dashboard, and audit log update together.'],
];

export default function Dashboard() {
  const { demo } = useAuth();
  const nav = useNavigate();
  const { data } = useAccounts(demo);
  const wf = useWorkflow();

  const acct = useMemo(() => {
    const a = data?.accounts ?? [];
    return {
      total: a.length,
      clients: a.filter((x) => x.type === 'client' || x.type === 'both').length,
      vendors: a.filter((x) => x.type === 'vendor' || x.type === 'both').length,
    };
  }, [data]);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Command Dashboard</div>
          <div className="page-sub">Intake health, open work, calendar commitments, AI risk, and synchronized client activity.</div>
        </div>
        <div className="global-actions">
          <button className="btn" onClick={wf.generateDigest}>Generate AI Digest</button>
          <button className="btn primary" onClick={() => nav('/intake')}>New Intake</button>
        </div>
      </div>

      <div className="flow-strip">
        {FLOW.map(([t, s], i) => (
          <div key={t} className={`flow-step${i + 1 === wf.flowStep ? ' active' : ''}${i + 1 < wf.flowStep ? ' done' : ''}`}>
            <strong>{t}</strong><span>{s}</span>
          </div>
        ))}
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-label">Open Intake</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{wf.metrics.openIntake}</div><div className="metric-note">Requires AI triage / client detail</div></div>
        <div className="metric-card"><div className="metric-label">Active Tasks</div><div className="metric-value">{wf.metrics.activeTasks}</div><div className="metric-note">Across all departments</div></div>
        <div className="metric-card"><div className="metric-label">Blocked Jobs</div><div className="metric-value" style={{ color: 'var(--rd)' }}>{wf.metrics.blocked}</div><div className="metric-note">Awaiting info, material, or approval</div></div>
        <div className="metric-card"><div className="metric-label">AI Risk Score</div><div className="metric-value" style={{ color: 'var(--or)' }}>{wf.metrics.risk}</div><div className="metric-note">Medium · logistics &amp; proof delays</div></div>
      </div>

      <div className="grid sidebar-layout">
        <div className="card">
          <div className="card-header">
            <div><h3>Live Work Queue</h3><p>Tasks created by intake, AI, and department engines.</p></div>
            <button className="btn small" onClick={() => nav('/tasks')}>Open Board</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Task</th><th>Client</th><th>Owner</th><th>Dept</th><th>Status</th><th>Due</th></tr></thead>
              <tbody>
                {wf.tasks.slice(0, 7).map((t) => (
                  <tr key={t.id} onClick={() => nav('/tasks')}>
                    <td><strong>{t.title}</strong><div className="sub">{t.id}</div></td>
                    <td>{t.client}</td>
                    <td>{t.owner}</td>
                    <td><Tag kind="dp" value={t.dept} /></td>
                    <td><Tag kind="st" value={t.status} /></td>
                    <td>{t.due}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div><h3>AI Operations Digest</h3><p>Current recommendations and warnings.</p></div></div>
          <div className="card-body">
            <div className="ai-panel">
              <h4>MFX OS Chief AI</h4>
              <div className="ai-output">{wf.digest}</div>
            </div>
            <div className="legend">
              {['csr', 'prepress', 'logistics', 'production', 'quality'].map((d) => <Tag key={d} kind="dp" value={d} />)}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div><h3>Account Directory</h3><p>{data?.live ? `Live · ${data.sources.join(' + ')}` : 'Demo dataset'} — clients &amp; vendors in one place.</p></div>
          <button className="btn small" onClick={() => nav('/accounts')}>View accounts →</button>
        </div>
        <div className="card-body">
          <div className="grid cols-3">
            <div className="metric-card"><div className="metric-label">Total Accounts</div><div className="metric-value" style={{ color: 'var(--ac)' }}>{acct.total}</div><div className="metric-note">Unified customer/client/vendor records</div></div>
            <div className="metric-card"><div className="metric-label">Clients</div><div className="metric-value">{acct.clients}</div><div className="metric-note">Active buying accounts</div></div>
            <div className="metric-card"><div className="metric-label">Vendors</div><div className="metric-value">{acct.vendors}</div><div className="metric-note">Material &amp; service suppliers</div></div>
          </div>
        </div>
      </div>
    </>
  );
}
